import Cookies from 'js-cookie';

import { CookieMap } from '@/core/constants';

const isSecureContext = (): boolean =>
  import.meta.env.VITE_ENV === 'production';

function getSecureCookieName(baseName: string): string {
  return isSecureContext() ? `__Secure-${baseName}` : baseName;
}

/**
 * Smart cookie map that automatically resolves secure cookie names.
 * Use this instead of direct Cookies.get() calls.
 *
 * Designed for client-side use with js-cookie.
 */
class HorizonClientCookies {
  constructor() {}

  get<K extends keyof typeof CookieMap>(cookieName: K) {
    const baseName = CookieMap[cookieName];
    const secureName = getSecureCookieName(baseName);

    const secureValue = Cookies.get(secureName);
    if (secureValue !== undefined) {
      return { name: secureName, value: secureValue };
    }

    const baseValue = Cookies.get(baseName);
    if (baseValue !== undefined) {
      return { name: baseName, value: baseValue };
    }

    return undefined;
  }

  getValue<K extends keyof typeof CookieMap>(
    cookieName: K,
  ): string | undefined {
    return this.get(cookieName)?.value;
  }

  has<K extends keyof typeof CookieMap>(cookieName: K): boolean {
    return !!this.get(cookieName);
  }

  set<K extends keyof typeof CookieMap>(
    cookieName: K,
    value: string,
    options?: Cookies.CookieAttributes,
  ) {
    const baseName = CookieMap[cookieName];
    const secureName = getSecureCookieName(baseName);
    const resolvedOptions = isSecureContext()
      ? { ...options, secure: true }
      : options;
    return Cookies.set(secureName, value, resolvedOptions);
  }

  delete<K extends keyof typeof CookieMap>(
    cookieName: K,
    options?: Cookies.CookieAttributes,
  ) {
    const baseName = CookieMap[cookieName];

    // Always purge BOTH name variants, regardless of environment. The
    // environment check only tells us which name `set()` would use *now*; a
    // stale cookie under the other name (e.g. a `__Secure-` cookie left over
    // from a production visit to the same domain) would otherwise survive.
    //
    // A `__Secure-`-prefixed cookie can only be (re)written over HTTPS WITH the
    // Secure attribute — including the expiring Set-Cookie that removes it.
    // js-cookie omits Secure by default, so the browser silently rejects the
    // deletion and the cookie survives; pass secure: true for that variant.
    Cookies.remove(`__Secure-${baseName}`, { ...options, secure: true });
    Cookies.remove(baseName, options);
  }

  getAll() {
    const allCookies: { name: string; value: string }[] = [];
    const cookieObject = Cookies.get();
    for (const [name, value] of Object.entries(cookieObject)) {
      allCookies.push({ name, value });
    }
    return allCookies;
  }
}

/**
 * Enhanced cookies() function that returns a smart cookie map.
 * Drop-in replacement for Cookies.get() that handles secure prefixes automatically.
 */
export function cookies() {
  return new HorizonClientCookies();
}

/**
 * Utility to get a cookie value with automatic secure name resolution.
 * For quick one-off cookie reads.
 */
export function getCookie<K extends keyof typeof CookieMap>(
  cookieName: K,
): string | undefined {
  return cookies().getValue(cookieName);
}
